// modules/DropSystem.js
// 副本掉落系统模块
import DebugHelper from './DebugHelper.js';
import { deepClone } from './utils/PlayerDataSync.js';
import GameStateStore from './GameStateStore.js';

export default class DropSystem {
    /**
     * 根据ID获取物品数据（从items.json或vehicle_equipment.json）
     * @param {string} itemId - 物品ID
     * @param {Phaser.Scene} scene - 场景对象（用于访问缓存）
     * @returns {Object|null} 物品数据
     */
    static getItemById(itemId, scene = null) {
        if (!itemId) return null;
        
        // 尝试从items.json获取
        let itemsData = null;
        if (scene && scene.cache && scene.cache.json) {
            itemsData = scene.cache.json.get('items');
        } else if (GameStateStore.get('cache')) {
            itemsData = GameStateStore.get('cache').json?.get('items');
        }
        
        if (itemsData && itemsData.items) {
            const item = itemsData.items.find(i => i.id === itemId);
            if (item) {
                return deepClone(item);
            }
        }
        
        // 尝试从vehicle_equipment.json获取
        let vehicleEquipmentData = null;
        if (scene && scene.cache && scene.cache.json) {
            vehicleEquipmentData = scene.cache.json.get('vehicle_equipment');
        } else if (GameStateStore.get('cache')) {
            vehicleEquipmentData = GameStateStore.get('cache').json?.get('vehicle_equipment');
        }
        
        if (vehicleEquipmentData) {
            // 检查vehicleEquipment数组
            if (vehicleEquipmentData.vehicleEquipment) {
                const equipment = vehicleEquipmentData.vehicleEquipment.find(e => e.id === itemId);
                if (equipment) {
                    return deepClone(equipment);
                }
            }
            // 检查ammo数组
            if (vehicleEquipmentData.ammo) {
                const ammo = vehicleEquipmentData.ammo.find(a => a.id === itemId);
                if (ammo) {
                    return deepClone(ammo);
                }
            }
        }
        
        return null;
    }
    
    /**
     * 计算掉落（基于敌人数据）
     * @param {Object} enemy - 敌人对象
     * @param {number} difficulty - 难度系数（1.0为基础，越高掉落越好）
     * @param {Phaser.Scene} scene - 场景对象（用于获取物品数据）
     * @returns {Array<Object>} 掉落物品列表
     */
    static calculateDrops(enemy, difficulty = 1.0, scene = null) {
        if (!enemy || !enemy.drops) {
            return [];
        }
        
        const drops = [];
        
        enemy.drops.forEach(dropConfig => {
            // 统一处理：将 dropConfig 转换为标准格式
            let config = null;
            if (typeof dropConfig === 'string') {
                // 如果直接是字符串ID，转换为对象格式
                config = { id: dropConfig, chance: 100 };
            } else {
                config = dropConfig;
            }
            
            // 如果配置中没有 id，跳过
            if (!config || !config.id) {
                DebugHelper.warn(`DropSystem: 掉落配置无效:`, dropConfig);
                return;
            }
            
            // 获取物品完整数据
            let itemData = null;

            itemData = this.getItemById(config.id, scene);
            if (!itemData) {
                DebugHelper.warn(`DropSystem: 未找到物品 ${config.id}`);
                return;
            }
            // 验证获取到的数据是否完整
            if (!itemData.name || !itemData.type) {
                DebugHelper.warn(`DropSystem: 物品 ${config.id} 数据不完整 - name: ${itemData.name}, type: ${itemData.type}`);
                return;
            }
            
            // 计算实际掉落概率（考虑难度）
            const baseChance = config.chance || 0;
            const actualChance = Math.min(100, baseChance * difficulty);
            
            // 随机判断是否掉落
            if (Math.random() * 100 < actualChance) {
                // 计算掉落数量
                const count = config.count || 1;
                const minCount = config.minCount !== undefined ? config.minCount : count;
                const maxCount = config.maxCount !== undefined ? config.maxCount : count;
                const actualCount = Math.floor(Math.random() * (maxCount - minCount + 1)) + minCount;
                
                // 合并物品数据和掉落配置
                // 物品完整数据（name, type, description等）优先，然后添加掉落相关属性
                const drop = {
                    ...itemData, // 物品完整数据（包括 name, type, description 等）
                    chance: config.chance, // 掉落几率
                    count: actualCount, // 实际掉落数量
                };
                
                // 添加其他掉落相关属性（如果存在）
                if (config.tier !== undefined) drop.tier = config.tier;
                if (config.minCount !== undefined) drop.minCount = config.minCount;
                if (config.maxCount !== undefined) drop.maxCount = config.maxCount;
                
                drops.push(drop);
            }
        });
        
        return drops;
    }
    
    /**
     * 应用掉落到玩家背包
     * @param {Object} player - 玩家对象
     * @param {Array<Object>} drops - 掉落物品列表
     * @returns {Object} 应用结果 {success: boolean, items: Array, message: string}
     */
    static applyDrops(player, drops) {
        if (!player || !drops || drops.length === 0) {
            return {
                success: false,
                items: [],
                message: '没有掉落物品'
            };
        }
        
        if (!player.inventory) {
            player.inventory = [];
        }
        
        const addedItems = [];
        
        drops.forEach(drop => {
            // 处理随机掉落
            if (drop.id === 'random_equipment') {
                // 随机装备：从装备池中选择
                const equipment = this.getRandomEquipment(drop.tier);
                if (equipment) {
                    player.inventory.push(equipment);
                    addedItems.push(equipment);
                }
            } else if (drop.id === 'random_fragment') {
                // 随机碎片：生成碎片
                const fragments = this.generateRandomFragments(drop.count, drop.tier);
                fragments.forEach(fragment => {
                    player.inventory.push(fragment);
                    addedItems.push(fragment);
                });
            } else if (drop.id && drop.id.endsWith('_fragment')) {
                // 指定装备的碎片：从装备ID生成碎片
                const equipmentId = drop.id.replace('_fragment', '');
                const equipmentData = GameStateStore.get('cache')?.json?.get('vehicle_equipment');
                
                if (equipmentData && equipmentData.vehicleEquipment) {
                    const equipment = equipmentData.vehicleEquipment.find(eq => eq.id === equipmentId);
                    if (equipment && equipment.name) {
                        const count = drop.count || 1;
                        for (let i = 0; i < count; i++) {
                            const fragment = {
                                id: `${equipment.id}_fragment`,
                                name: `${equipment.name}碎片`,
                                type: 'fragment',
                                equipmentId: equipment.id,
                                tier: equipment.tier || 1,
                                description: `用于合成 ${equipment.name} 的碎片`
                            };
                            player.inventory.push(fragment);
                            addedItems.push(fragment);
                        }
                    } else {
                        DebugHelper.warn(`DropSystem: 找不到装备 ${equipmentId}，无法生成碎片`);
                    }
                } else {
                    DebugHelper.warn('DropSystem: 装备数据未加载，无法生成碎片');
                }
            } else {
                // 普通掉落：直接添加
                for (let i = 0; i < (drop.count || 1); i++) {
                    const item = { ...drop };
                    delete item.chance;
                    delete item.count;
                    delete item.minCount;
                    delete item.maxCount;
                    player.inventory.push(item);
                    addedItems.push(item);
                }
            }
        });
        
        DebugHelper.log(`DropSystem: 应用 ${addedItems.length} 个掉落物品`);
        
        return {
            success: true,
            items: addedItems,
            message: `获得了 ${addedItems.length} 个物品！`
        };
    }
    
    /**
     * 获取随机装备（根据等级）
     * @param {number} tier - 装备等级
     * @returns {Object|null} 装备对象
     */
    static getRandomEquipment(tier = 1) {
        // 从缓存中获取战车装备数据
        const equipmentData = GameStateStore.get('cache')?.json?.get('vehicle_equipment');
        if (!equipmentData || !equipmentData.vehicleEquipment) {
            return null;
        }
        
        // 筛选对应等级的装备
        const tierEquipment = equipmentData.vehicleEquipment.filter(
            eq => eq.tier === tier
        );
        
        if (tierEquipment.length === 0) {
            return null;
        }
        
        // 随机选择一个
        const randomIndex = Math.floor(Math.random() * tierEquipment.length);
        return deepClone(tierEquipment[randomIndex]);
    }
    
    /**
     * 生成随机碎片
     * @param {number} count - 碎片数量
     * @param {number} tier - 装备等级
     * @returns {Array<Object>} 碎片列表
     */
    static generateRandomFragments(count, tier = 1) {
        const fragments = [];
        
        // 从缓存中获取战车装备数据
        const equipmentData = GameStateStore.get('cache')?.json?.get('vehicle_equipment');
        if (!equipmentData || !equipmentData.vehicleEquipment) {
            return fragments;
        }
        
        // 筛选对应等级的装备
        const tierEquipment = equipmentData.vehicleEquipment.filter(
            eq => eq.tier === tier
        );
        
        if (tierEquipment.length === 0) {
            return fragments;
        }
        
        // 随机生成碎片
        for (let i = 0; i < count; i++) {
            const randomIndex = Math.floor(Math.random() * tierEquipment.length);
            const equipment = tierEquipment[randomIndex];
            
            // 确保equipment存在且有name属性
            if (!equipment || !equipment.name || !equipment.id) {
                DebugHelper.warn('DropSystem: 装备数据无效，跳过碎片生成', equipment);
                continue;
            }
            
            fragments.push({
                id: `${equipment.id}_fragment`,
                name: `${equipment.name}碎片`,
                type: 'fragment',
                equipmentId: equipment.id,
                tier: tier,
                description: `用于合成 ${equipment.name} 的碎片`
            });
        }
        
        return fragments;
    }
}

